import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "w-full mb-4"
}
const _hoisted_2 = { class: "relative flex items-center w-full h-[22px] max-h-[22px] bg-[#f3f0e9] rounded-full p-[2px] border border-[#e8e2d8] select-none" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { class: "flex flex-wrap w-full justify-center" }
const _hoisted_5 = ["onClick", "onMouseover"]
const _hoisted_6 = ["src", "srcset"]
const _hoisted_7 = { class: "text-parameter-text text-[11px]" }

import ItemV3ParameterBox from "@/components/itemV3/parameter/itemV3ParameterBox.vue";
import {computed, inject, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";
import {useI18n} from "vue-i18n";
import {ParametersVariations} from "@/assets/ts/parametersVariations";
import {getCurrentText} from "@/assets/ts/itemText";

const imageWidthPercent: number = 30
const boxIdx = "color"

const variantIdx = "discs.*.color"


export default /*@__PURE__*/_defineComponent({
  __name: 'itemV3DiscsColor',
  props: ["item"],
  emits: ["patch"],
  setup(__props, { emit: __emit }) {


const {t} = useI18n();

const emit = __emit;
const props = __props;

const store = useStore();

const loadItemVariants = inject<any>("loadItemVariants");

const itemIdx = inject<any>("itemIdx");
const session = inject<any>("session");

const loading = ref(true)

/**
 * Parameter box header images
 */

const mainMetal = computed(() => store.getters["session/getMainMetal"])
const getHeaderImage = (size: string = "600x300", withBg = false) => {
  if (withBg) {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${mainMetal.value}_${boxIdx}.webp`
  } else {
    return `https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/menu/${size}/${boxIdx}.webp`
  }
}

/**
 * session
 */

const configureAll = computed(() => store.getters["session/getConfigureAll"])
const parametersVariations = new ParametersVariations(variantIdx, configureAll, session, itemIdx, loadItemVariants)

const currentDiscsColorText = computed(() => {
  return getCurrentText(configureAll.value ? -1 : itemIdx.value, variantIdx)
})

const open = ref(false)

const discVariants = computed(() => open.value ? parametersVariations.getVariants() : [])

/**
 * color variants
 */

const activeDiscIdx = ref(0)

const discSliderStyle = computed(() => {
  const count = discVariants.value.length;
  if (count === 0 || activeDiscIdx.value < 0 || activeDiscIdx.value >= count) return {display: 'none'};

  const width = 100 / count;
  const left = activeDiscIdx.value * width;

  return {
    width: `calc(${width}% - 4px)`,
    left: `calc(${left}% + 2px)`,
  };
});

const colorVariantsOfDisc = computed(() => {
  if (!discVariants.value.length || discVariants.value.length < activeDiscIdx.value) return []
  return discVariants.value[activeDiscIdx.value].values
})

const activeDisc = computed(() => {
  if (!discVariants.value.length || discVariants.value.length < activeDiscIdx.value) return []
  return discVariants.value[activeDiscIdx.value]
})

const matchedDiscIdx = computed(() => {
  if (!discVariants.value.length || discVariants.value.length < activeDiscIdx.value) return -1
  return discVariants.value[activeDiscIdx.value].values.findIndex((v: any) => v.match)
})

/**
 * image
 */

const hoverColorIdx = ref(-1)

const setHoverColorIdx = () => {
  if (matchedDiscIdx.value !== -1 && colorVariantsOfDisc.value.length >= matchedDiscIdx.value) {
    hoverColorIdx.value = matchedDiscIdx.value
  }
}

watch(activeDisc, () => {
  setHoverColorIdx()
})

watch(props.item, () => {
  setHoverColorIdx()
})

const imageUrl = computed(() => parametersVariations.imageUrl(colorVariantsOfDisc.value, hoverColorIdx.value))

const getColorImageUrl = (color: string, size: number) => {
  return 'https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/parameters/colors/' + size + 'x' + size + '/' + color.toLowerCase() + '.webp'
}

const checkImageLoaded = (url: string) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(true);
    img.src = url;
  });
};

const checkAllLoaded = async () => {
  const discs = discVariants.value;
  if (!discs.length) return;

  const allListsCreated = discs.every((disc: any) => disc.values && disc.values.length > 0);
  if (!allListsCreated) return;

  const imageUrls = discs.flatMap((disc: any) => disc.values.map((color: any) => getColorImageUrl(color.idx, 300)));
  await Promise.all(imageUrls.map((url: string) => checkImageLoaded(url)));

  loading.value = false;
};

watch(discVariants, () => {
  checkAllLoaded();
  if (discVariants.value.length > 0) {
    loading.value = true;
  }
}, {immediate: true, deep: true});

/**
 * Patch
 */

const handleValuePatch = (actionIdx: string, value: any) => {
  emit('patch', {
    action: actionIdx,
    value: value.idx,
    certificated_diamond: value.certificated_diamond
  });
};

/**
 * tip texts
 */

const getColorTipTitle = computed(() => {
  if (activeDisc.value && activeDisc.value.values[hoverColorIdx.value]) {
    const key = activeDisc.value.values[hoverColorIdx.value].additional_value.toUpperCase() + "-" + activeDisc.value.values[hoverColorIdx.value].idx.toUpperCase()
    return t('components.parameters.tips.colorMetals.' + key + '.title')
  } else {
    return ''
  }
})

const getColorTipText = computed(() => {
  if (activeDisc.value && activeDisc.value.values[hoverColorIdx.value]) {
    const key = activeDisc.value.values[hoverColorIdx.value].additional_value.toUpperCase() + "-" + activeDisc.value.values[hoverColorIdx.value].idx.toUpperCase()
    return t('components.parameters.tips.colorMetals.' + key + '.text')
  } else {
    return ''
  }
})

const getColorTipImageUrl = computed(() => {
  if (activeDisc.value && activeDisc.value.values[hoverColorIdx.value]) {
    const key = activeDisc.value.values[hoverColorIdx.value].additional_value.toUpperCase() + "-" + activeDisc.value.values[hoverColorIdx.value].idx.toUpperCase()
    return "https://images-api-prod-images.s3.eu-central-1.amazonaws.com/parts/tips/metal/300x300/" + key + ".webp"

  } else {
    return ''
  }
})

onMounted(() => {
      setHoverColorIdx()
    }
)


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ItemV3ParameterBox, {
    open: open.value,
    "onUpdate:open": _cache[1] || (_cache[1] = ($event: any) => ((open).value = $event)),
    "image-width-percent": imageWidthPercent,
    image: imageUrl.value,
    "header-image": getHeaderImage(),
    "header-image-srcset": getHeaderImage() + ' 300w, ' + getHeaderImage('1200x600') +' 600w',
    title: "Farbe",
    subtitle: currentDiscsColorText.value || 'Wählen Sie die Metalfarbe für Ihre Trauringe',
    loading: loading.value,
    "tip-title": getColorTipTitle.value,
    "tip-text": getColorTipText.value,
    "tip-image": getColorTipImageUrl.value
  }, {
    main: _withCtx(() => [
      (discVariants.value.length > 1)
        ? (_openBlock(), _createElementBlock("nav", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", {
                class: "absolute top-[2px] bottom-[2px] bg-brand-main rounded-full transition-all duration-300 ease-in-out shadow-xs pointer-events-none",
                style: _normalizeStyle(discSliderStyle.value)
              }, null, 4),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(discVariants.value, (disc, discIdx) => {
                return (_openBlock(), _createElementBlock("button", {
                  key: discIdx,
                  type: "button",
                  class: _normalizeClass(["text-[10px] tracking-wided relative flex-1 flex cursor-pointer items-center justify-center text-xs transition-colors duration-200 z-10 font-normal leading-none h-full", activeDiscIdx.value === discIdx ? 'text-white font-medium' : 'text-gray-500 hover:text-gray-700']),
                  onClick: ($event: any) => (activeDiscIdx.value = discIdx)
                }, " Scheibe " + _toDisplayString(discIdx + 1), 11, _hoisted_3))
              }), 128))
            ])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("ul", _hoisted_4, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(colorVariantsOfDisc.value, (color, colorIdx) => {
          return (_openBlock(), _createElementBlock("li", {
            key: colorIdx,
            class: _normalizeClass(["cursor-pointer flex flex-col items-center py-2 px-1 rounded-2xl hover:bg-[#f3f0e9]", {
              'border-2 border-brand-main bg-[#f3f0e9]': color.match,
              'border-2 border-transparent': !color.match
            }]),
            onClick: ($event: any) => (handleValuePatch(activeDisc.value.idx, color)),
            onMouseover: ($event: any) => (hoverColorIdx.value = colorIdx as number),
            onMouseleave: _cache[0] || (_cache[0] = ($event: any) => (hoverColorIdx.value = matchedDiscIdx.value))
          }, [
            _createElementVNode("img", {
              src: getColorImageUrl(color.idx, 300),
              srcset: getColorImageUrl(color.idx, 600),
              class: "w-10 aspect-square"
            }, null, 8, _hoisted_6),
            _createElementVNode("span", _hoisted_7, _toDisplayString(color.title), 1)
          ], 42, _hoisted_5))
        }), 128))
      ])
    ]),
    _: 1
  }, 8, ["open", "image", "header-image", "header-image-srcset", "subtitle", "loading", "tip-title", "tip-text", "tip-image"]))
}
}

})